// Import the functions you need from the SDKs you need
import { Capacitor } from '@capacitor/core';
import { initializeApp } from 'firebase/app';
import type { Messaging } from 'firebase/messaging';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import type { MessagePayload } from 'firebase/messaging/sw';
import { checkFirebaseMessagingSupport, isUnsupportedBrowser, getBrowserInfo } from './helpers/browserSupport';

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const firebaseApp = initializeApp(firebaseConfig);

// Check if Firebase Messaging is supported before initializing
export const isMessagingSupported = async (): Promise<boolean> => {
  if (Capacitor.isNativePlatform()) return false;

  // Quick browser support check
  if (!checkFirebaseMessagingSupport() || isUnsupportedBrowser()) {
    console.warn('Browser does not meet basic requirements for Firebase Messaging:', getBrowserInfo());
    return false;
  }

  try {
    return await isSupported();
  } catch (error) {
    console.warn('Firebase Messaging is not supported in this browser:', error);
    return false;
  }
};

// Initialize messaging only if supported
let messaging: Messaging | null = null;
isMessagingSupported()
  .then((supported) => {
    if (supported) {
      messaging = getMessaging(firebaseApp);
    } else {
      console.warn('Firebase Messaging not initialized - browser not supported');
    }
  })
  .catch((error) => {
    console.warn('Error checking Firebase Messaging support:', error);
  });

export const getOrRegisterServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    return window.navigator.serviceWorker.getRegistration('/firebase-push-notification-scope').then((serviceWorker) => {
      if (serviceWorker) return serviceWorker;
      //return window.navigator.serviceWorker.register('/firebase-messaging-sw.js?v=1', {
      //  scope: '/firebase-push-notification-scope',
      //});
    });
  }
  throw new Error('The browser doesn`t support service worker.');
};

export const getFirebaseToken = async () => {
  // Check if messaging is supported and initialized
  if (!messaging) {
    const supported = await isMessagingSupported();
    if (!supported) {
      throw new Error('Firebase Messaging is not supported in this browser');
    }
  }

  return getOrRegisterServiceWorker().then(
    (serviceWorkerRegistration) =>
      messaging && getToken(messaging, { vapidKey: import.meta.env.VITE_VAPID_KEY, serviceWorkerRegistration }),
  );
};

export const onForegroundMessage = () => {
  // Check if messaging is available
  if (!messaging) {
    return Promise.reject(new Error('Firebase Messaging is not supported in this browser'));
  }

  return new Promise<MessagePayload>((resolve) => onMessage(messaging!, (payload) => resolve(payload)));
};
